import React, { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  Grid,
  Paper,
  Card,
  CardContent,
  IconButton,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  LinearProgress,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  CircularProgress,
  TextField,
  InputAdornment,
  Menu,
  MenuItem,
} from '@mui/material'
import {
  Refresh as RefreshIcon,
  Business as BusinessIcon,
  People as PeopleIcon,
  Devices as DevicesIcon,
  TrendingUp as TrendingUpIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  ArrowForward as ArrowForwardIcon,
  Download as DownloadIcon,
  FilterList as FilterIcon,
  Search as SearchIcon,
} from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import RegisterOrgForm from '../../components/auth/RegisterOrgForm.jsx'
import { useLoading } from '../../hooks'
import { StatusChip, SubscriptionChip } from '../../components/common/Chips'
import { organizationApi } from '../../api/organizationApi'
import { dashboardApi } from '../../api/dashboardApi'
import TableSkeleton from '../../components/common/Skeleton/TableSkeleton'

// Mock Data for Dashboard Activity
const mockActivities = [
  { id: 1, action: 'Organization registered', entity: 'Tech Solutions', user: 'System', time: '10 min ago' },
  { id: 2, action: 'User created', entity: 'Rajesh Kumar', user: 'Admin', time: '25 min ago' },
  { id: 3, action: 'Device registered', entity: 'Tablet-045', user: 'System', time: '1 hour ago' },
  { id: 4, action: 'Password reset', entity: 'priya@reliance.com', user: 'User', time: '2 hours ago' },
  { id: 5, action: 'Subscription updated', entity: 'Tata Motors', user: 'Admin', time: '3 hours ago' },
  { id: 6, action: 'Organization activated', entity: 'Aditya Birla', user: 'Admin', time: '4 hours ago' },
  { id: 7, action: 'Report generated', entity: 'Monthly Analytics', user: 'System', time: '5 hours ago' },
]

const SuperAdminDashboard = () => {
  const navigate = useNavigate()
  const [registerOpen, setRegisterOpen] = useState(false)
  const [editOpen, setEditOpen] = useState(false)
  const { loading, startLoading, stopLoading } = useLoading()
  const [organizations, setOrganizations] = useState([])
  const [searchTerm, setSearchTerm] = useState('')

  const [filterStatus, setFilterStatus] = useState('all')
  const [filterMenuAnchor, setFilterMenuAnchor] = useState(null)

  // State for organization details dialog
  const [viewDialogOpen, setViewDialogOpen] = useState(false)
  const [selectedOrg, setSelectedOrg] = useState(null)

  // State for notifications
  const [snackbarOpen, setSnackbarOpen] = useState(false)
  const [snackbarMessage, setSnackbarMessage] = useState('')
  const [snackbarSeverity, setSnackbarSeverity] = useState('success')

  const [stats, setStats] = useState({
    totalOrganizations: 0,
    activeOrganizations: 0,
    pendingOrganizations: 0,
    suspendedOrganizations: 0,
    totalUsers: 0,
    activeUsers: 0,
    inactiveUsers: 0,
    totalDevices: 0,
    activeDevices: 0,
    inactiveDevices: 0,
    systemUptime: 0,
  })

  // Fetch organizations from API
  useEffect(() => {
    const initData = async () => {
      startLoading()
      await Promise.all([fetchOrganizations(), fetchStats()])
      stopLoading()
    }
    initData()
  }, [])

  const fetchOrganizations = async () => {
    try {
      const response = await organizationApi.listOrganizations()
      setOrganizations(response.data || [])
    } catch (error) {
      console.error('Failed to fetch organizations:', error)
      setSnackbarMessage('Failed to load organizations')
      setSnackbarSeverity('error')
      setSnackbarOpen(true)
    }
  }

  const fetchStats = async () => {
    try {
      const response = await dashboardApi.getAllStats()
      const data = response.data
      setStats({
        totalOrganizations: data.orgs.total_orgs,
        activeOrganizations: data.orgs.active_orgs,
        pendingOrganizations: 0,
        suspendedOrganizations: data.orgs.inactive_orgs,
        totalUsers: data.users.total_users,
        activeUsers: data.users.active_users,
        inactiveUsers: data.users.inactive_users,
        totalDevices: data.devices.total_devices,
        activeDevices: data.devices.active_devices,
        inactiveDevices: data.devices.inactive_devices,
        systemUptime: parseFloat(data.system_uptime),
      })
    } catch (error) {
      console.error('Failed to fetch stats:', error)
    }
  }

  const handleRefresh = async () => {
    startLoading()
    await Promise.all([fetchOrganizations(), fetchStats()])
    stopLoading()
    setSnackbarMessage('Data refreshed successfully!')
    setSnackbarSeverity('success')
    setSnackbarOpen(true)
  }

  const handleRegisterSuccess = async (orgData) => {
    // Refresh the organizations list from the API
    await fetchOrganizations()
    setRegisterOpen(false)
    setSnackbarMessage('Organization registered successfully!')
    setSnackbarSeverity('success')
    setSnackbarOpen(true)
  }

  const handleEditSuccess = (updatedData) => {
    // Update organization in the list locally for better UX
    setOrganizations(prev =>
      prev.map(org =>
        org.id === selectedOrg.id
          ? { ...org, ...updatedData }
          : org
      )
    )
    setEditOpen(false)
    setSnackbarMessage(`Organization "${updatedData.name}" updated successfully!`)
    setSnackbarSeverity('success')
    setSnackbarOpen(true)
    setSelectedOrg(null)
  }

  const handleViewOrganization = (org) => {
    setSelectedOrg(org)
    setViewDialogOpen(true)
  }

  const handleEditClick = async (org) => {
    try {
      const response = await organizationApi.getOrganizationDetails(org.id)
      setSelectedOrg(response.data)
      setEditOpen(true)
    } catch (error) {
      console.error('Failed to fetch details:', error)
      setSnackbarMessage('Failed to load organization details')
      setSnackbarSeverity('error')
      setSnackbarOpen(true)
    }
  }

  const handleDeleteClick = async (org) => {
    if (window.confirm(`Are you sure you want to delete ${org.name}? This action cannot be undone.`)) {
      try {
        await organizationApi.deleteOrganization(org.id)
        // Soft delete: update status to inactive
        setOrganizations(prev => prev.map(o =>
          o.id === org.id ? { ...o, status: 'inactive' } : o
        ))
        setSnackbarMessage(`Organization "${org.name}" marked as inactive`)
        setSnackbarSeverity('success')
        setSnackbarOpen(true)
      } catch (error) {
        console.error('Error deleting organization:', error)
        setSnackbarMessage('Failed to delete organization')
        setSnackbarSeverity('error')
        setSnackbarOpen(true)
      }
    }
  }

  // Filter and sort organizations based on search and status (newest first)
  const filteredOrganizations = [...organizations]
    .filter(org => {
      const matchesSearch = org.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (org.contact_email && org.contact_email.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (org.contact_name && org.contact_name.toLowerCase().includes(searchTerm.toLowerCase()))

      const matchesFilter = filterStatus === 'all' || org.status === filterStatus

      return matchesSearch && matchesFilter
    })
    .sort((a, b) => {
      const dateA = a.created_at ? new Date(a.created_at).getTime() : 0;
      const dateB = b.created_at ? new Date(b.created_at).getTime() : 0;

      if (dateA !== dateB) {
        return dateB - dateA; // Descending by date
      }
      // Fallback to sorting by ID descending if dates are equal or missing
      if (a.id !== b.id) {
        return b.id > a.id ? 1 : -1;
      }
      return 0;
    })

  return (
    <Box sx={{ p: { xs: 1, md: 3 } }}>
      {/* Register Form Dialog */}
      <RegisterOrgForm
        open={registerOpen}
        onClose={() => setRegisterOpen(false)}
        onSuccess={handleRegisterSuccess}
      />

      {/* Edit Form Dialog */}
      <RegisterOrgForm
        open={editOpen}
        onClose={() => {
          setEditOpen(false)
          setSelectedOrg(null)
        }}
        onSuccess={handleEditSuccess}
        editMode={true}
        initialData={selectedOrg}
      />

      {/* View Details Dialog */}
      <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} maxWidth="md" fullWidth disableRestoreFocus>
        <DialogTitle>Organization Details</DialogTitle>
        <DialogContent>
          {selectedOrg && (
            <Box sx={{ pt: 2 }}>
              <Grid container spacing={3}>
                <Grid size={{ xs: 12 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Avatar sx={{ bgcolor: 'primary.main', width: 60, height: 60 }}>
                      <BusinessIcon />
                    </Avatar>
                    <Box>
                      <Typography variant="h5" fontWeight="bold">{selectedOrg.name}</Typography>
                      <Typography variant="body2" color="textSecondary">{selectedOrg.email}</Typography>
                    </Box>
                  </Box>
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="subtitle2" color="textSecondary">Contact Person</Typography>
                  <Typography variant="body1" fontWeight="medium">{selectedOrg.contact_name || 'N/A'}</Typography>
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="subtitle2" color="textSecondary">Phone</Typography>
                  <Typography variant="body1" fontWeight="medium">{selectedOrg.contact_phone || 'N/A'}</Typography>
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="subtitle2" color="textSecondary">Status</Typography>
                  <Box sx={{ mt: 1 }}>
                    <StatusChip status={selectedOrg.status} />
                  </Box>
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="subtitle2" color="textSecondary">Subscription</Typography>
                  <Box sx={{ mt: 1 }}>
                    <SubscriptionChip subscription={selectedOrg.subscription_plan} />
                  </Box>
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="subtitle2" color="textSecondary">Employees</Typography>
                  <Typography variant="h5" fontWeight="bold">{selectedOrg.employee_count !== undefined ? selectedOrg.employee_count.toLocaleString() : '0'}</Typography>
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="subtitle2" color="textSecondary">Devices</Typography>
                  <Typography variant="h5" fontWeight="bold">{selectedOrg.device_count !== undefined ? selectedOrg.device_count : '0'}</Typography>
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="subtitle2" color="textSecondary">Created Date</Typography>
                  <Typography variant="body1">{new Date(selectedOrg.created_at).toLocaleDateString()}</Typography>
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="subtitle2" color="textSecondary">Last Active</Typography>
                  <Typography variant="body1">
                    {selectedOrg.last_active ? new Date(selectedOrg.last_active).toLocaleDateString() : 'Never'}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
          <Button
            variant="contained"
            onClick={() => {
              setViewDialogOpen(false)
              handleEditClick(selectedOrg)
            }}
          >
            Edit Organization
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      <Paper sx={{ p: 3, borderRadius: 3, bgcolor: 'background.paper', boxShadow: 3 }}>
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h4" fontWeight="bold">
              Super Admin Dashboard
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                onClick={handleRefresh}
                disabled={loading}
                startIcon={<RefreshIcon />}
                size="small"
              >
                Refresh
              </Button>
            </Box>
          </Box>
          {loading && <LinearProgress sx={{ mb: 2 }} />}
        </Box>




        {/* Stats Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card variant="outlined" sx={{ height: '100%', borderRadius: 2 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
                    <BusinessIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="h4" fontWeight="bold">
                      {stats.totalOrganizations}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Total Organizations
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                  <Chip label={`${stats.activeOrganizations} Active`} size="small" color="success" variant="outlined" />
                  <Chip label={`${stats.suspendedOrganizations} Inactive`} size="small" color="error" variant="outlined" />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card variant="outlined" sx={{ height: '100%', borderRadius: 2 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar sx={{ bgcolor: 'success.main', mr: 2 }}>
                    <PeopleIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="h4" fontWeight="bold">
                      {stats.totalUsers}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Total Users
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                  <Chip label={`${stats.activeUsers} Active`} size="small" color="success" variant="outlined" />
                  <Chip label={`${stats.inactiveUsers} Inactive`} size="small" color="error" variant="outlined" />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card variant="outlined" sx={{ height: '100%', borderRadius: 2 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar sx={{ bgcolor: 'warning.main', mr: 2 }}>
                    <DevicesIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="h4" fontWeight="bold">
                      {stats.totalDevices}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Total Devices
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                  <Chip label={`${stats.activeDevices} Active`} size="small" color="success" variant="outlined" />
                  <Chip label={`${stats.inactiveDevices} Inactive`} size="small" color="error" variant="outlined" />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card variant="outlined" sx={{ height: '100%', borderRadius: 2 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar sx={{ bgcolor: 'secondary.main', mr: 2 }}>
                    <TrendingUpIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="h4" fontWeight="bold">
                      {stats.systemUptime}%
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      System Uptime
                    </Typography>
                  </Box>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={stats.systemUptime}
                  sx={{ mt: 1, height: 6, borderRadius: 3 }}
                  color="secondary"
                />
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Main Content - Full width table */}
        <Grid container spacing={3}>
          {/* Organizations Table - Full width */}
          {/* Organizations Table - Full width */}
          <Grid size={{ xs: 12 }} sx={{ width: '100%' }}>
            <Paper variant="outlined" sx={{ p: 3, borderRadius: 2, mb: 3 }}>
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, gap: 2, mb: 3 }}>
                <Typography variant="h6" fontWeight="bold">
                  Recent Organizations
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => setRegisterOpen(true)}
                  size="small"
                  sx={{ width: { xs: '100%', sm: 'auto' } }}
                >
                  Register Organization
                </Button>
              </Box>

              {/* Search and Filters */}
              <Box sx={{ p: 2, mb: 3 }}>
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, alignItems: 'center' }}>
                  <TextField
                    placeholder="Search organizations by name, email, or contact person..."
                    variant="outlined"
                    size="small"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                    }}
                    sx={{ flexGrow: 1, width: '100%' }}
                  />

                  <Box sx={{ display: 'flex', gap: 2, width: { xs: '100%', md: 'auto' } }}>
                    <Button
                      variant="outlined"
                      startIcon={<FilterIcon />}
                      size="small"
                      onClick={(e) => setFilterMenuAnchor(e.currentTarget)}
                      sx={{ flex: { xs: 1, md: 'none' } }}
                    >
                      Filter
                    </Button>

                    <Menu
                      anchorEl={filterMenuAnchor}
                      open={Boolean(filterMenuAnchor)}
                      onClose={() => setFilterMenuAnchor(null)}
                    >
                      <MenuItem onClick={() => { setFilterStatus('all'); setFilterMenuAnchor(null); }}>
                        All Status
                      </MenuItem>
                      <MenuItem onClick={() => { setFilterStatus('active'); setFilterMenuAnchor(null); }}>
                        Active
                      </MenuItem>
                      <MenuItem onClick={() => { setFilterStatus('inactive'); setFilterMenuAnchor(null); }}>
                        Inactive
                      </MenuItem>
                    </Menu>

                    <Button
                      variant="outlined"
                      startIcon={<DownloadIcon />}
                      size="small"
                      sx={{ flex: { xs: 1, md: 'none' } }}
                    >
                      Export
                    </Button>
                  </Box>
                </Box>
              </Box>

              {/* Table */}
              <Box sx={{ width: '100%', overflowX: 'auto' }}>
                <Table sx={{ width: '100%' }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold', width: '25%' }}>Organization</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', width: '15%' }}>Contact</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', width: '10%' }}>Employees</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', width: '10%' }}>Devices</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', width: '15%' }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', width: '15%' }}>Subscription</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', width: '10%' }} align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loading ? (
                      <TableSkeleton columns={7} rows={5} avatar />
                    ) : filteredOrganizations.length > 0 ? (
                      filteredOrganizations.slice(0, 10).map((org) => (
                        <TableRow
                          key={org.id}
                          hover
                          sx={{
                            '&:last-child td, &:last-child th': { border: 0 },
                            height: '70px'
                          }}
                        >
                          <TableCell>
                            <Box>
                              <Typography fontWeight="medium">{org.name}</Typography>
                              <Typography variant="caption" color="textSecondary">
                                {org.contact_email || 'N/A'}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Box>
                              <Typography variant="body2">{org.contact_name || 'N/A'}</Typography>
                              <Typography variant="caption" color="textSecondary">
                                {org.contact_phone || 'N/A'}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography fontWeight="medium" fontSize="16px">
                              {org.employee_count !== undefined ? org.employee_count.toLocaleString() : '0'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography fontWeight="medium" fontSize="16px">
                              {org.device_count !== undefined ? org.device_count : '0'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <StatusChip status={org.status} />
                          </TableCell>
                          <TableCell>
                            <SubscriptionChip subscription={org.subscription_plan} />
                          </TableCell>
                          <TableCell align="right">
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => handleViewOrganization(org)}
                                title="View Details"
                                sx={{
                                  borderRadius: '4px',
                                  padding: '4px',
                                  '&:hover': { backgroundColor: '#e3f2fd' }
                                }}
                              >
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => handleEditClick(org)}
                                title="Edit"
                                sx={{
                                  borderRadius: '4px',
                                  padding: '4px',
                                  '&:hover': { backgroundColor: '#e8f5e8' }
                                }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDeleteClick(org)}
                                title="Delete"
                                sx={{
                                  borderRadius: '4px',
                                  padding: '4px',
                                  '&:hover': { backgroundColor: '#ffebee' }
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                          <Typography color="textSecondary">
                            {searchTerm ? 'No organizations found matching your search.' : 'No organizations found.'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </Box>

              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" color="textSecondary">
                  Showing top {Math.min(10, filteredOrganizations.length)} of {filteredOrganizations.length} organizations
                </Typography>
                <Button
                  variant="contained"
                  endIcon={<ArrowForwardIcon />}
                  onClick={() => navigate('/super-admin/organizations')}
                  sx={{ minWidth: '180px' }}
                >
                  View All Organizations
                </Button>
              </Box>
            </Paper>
          </Grid>

          {/* Recent Activity & Quick Stats */}
          {/* <Grid size={{ xs: 12, md: 8 }} sx={{ width: '100%', overflow: 'hidden' }}>
            <Paper variant="outlined" sx={{ p: 3, borderRadius: 2, height: '100%' }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ mb: 2 }}>
                Recent Activity
              </Typography>
              <Box>
                {mockActivities.map((activity) => (
                  <Box
                    key={activity.id}
                    sx={{
                      py: 1.5,
                      px: 1,
                      borderBottom: '1px solid #f0f0f0',
                      '&:last-child': { borderBottom: 'none' },
                      '&:hover': { backgroundColor: 'action.hover' }
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <Typography variant="body2" fontWeight="medium">
                        {activity.action}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {activity.time}
                      </Typography>
                    </Box>
                    <Typography variant="caption" color="textSecondary">
                      {activity.entity} • by {activity.user}
                    </Typography>
                  </Box>
                ))}
              </Box>
              <Button
                fullWidth
                sx={{ mt: 2 }}
                endIcon={<ArrowForwardIcon />}
                onClick={() => navigate('/super-admin/audit-logs')}
              >
                View All Activities
              </Button>
            </Paper>
          </Grid> */}


          <Grid size={{ xs: 12 }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ mb: 2 }}>
              Quick Actions
            </Typography>
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 4 }}>
                <Card
                  variant="outlined"
                  sx={{
                    borderRadius: 2,
                    cursor: 'pointer',
                    bgcolor: 'background.paper',
                    transition: '0.3s',
                    '&:hover': {
                      bgcolor: '#e3f2fd',
                      borderColor: 'primary.main',
                      transform: 'translateY(-2px)',
                      boxShadow: 2,
                      '& .MuiTypography-root': { color: '#000000' },
                      '& .MuiTypography-body2': { color: 'rgba(0, 0, 0, 0.6)' }
                    }
                  }}
                  onClick={() => setRegisterOpen(true)}
                >
                  <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 3 }}>
                    <Avatar sx={{ bgcolor: 'primary.main', width: 50, height: 50 }}>
                      <AddIcon fontSize="medium" />
                    </Avatar>
                    <Box>
                      <Typography variant="h6" fontWeight="bold" color="text.primary">
                        Register Organization
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Add a new organization to the system
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid size={{ xs: 12, md: 4 }}>
                <Card
                  variant="outlined"
                  sx={{
                    borderRadius: 2,
                    cursor: 'pointer',
                    bgcolor: 'background.paper',
                    transition: '0.3s',
                    '&:hover': {
                      bgcolor: '#e8f5e9',
                      borderColor: 'success.main',
                      transform: 'translateY(-2px)',
                      boxShadow: 2,
                      '& .MuiTypography-root': { color: '#000000' },
                      '& .MuiTypography-body2': { color: 'rgba(0, 0, 0, 0.6)' }
                    }
                  }}
                  onClick={() => navigate('/super-admin/users')}
                >
                  <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 3 }}>
                    <Avatar sx={{ bgcolor: 'success.main', width: 50, height: 50 }}>
                      <PeopleIcon fontSize="medium" />
                    </Avatar>
                    <Box>
                      <Typography variant="h6" fontWeight="bold" color="text.primary">
                        Manage Users
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        View and manage system users
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid size={{ xs: 12, md: 4 }}>
                <Card
                  variant="outlined"
                  sx={{
                    borderRadius: 2,
                    cursor: 'pointer',
                    bgcolor: 'background.paper',
                    transition: '0.3s',
                    '&:hover': {
                      bgcolor: '#fff3e0',
                      borderColor: 'warning.main',
                      transform: 'translateY(-2px)',
                      boxShadow: 2,
                      '& .MuiTypography-root': { color: '#000000' },
                      '& .MuiTypography-body2': { color: 'rgba(0, 0, 0, 0.6)' }
                    }
                  }}
                  onClick={() => navigate('/super-admin/devices')}
                >
                  <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 3 }}>
                    <Avatar sx={{ bgcolor: 'warning.main', width: 50, height: 50 }}>
                      <DevicesIcon fontSize="medium" />
                    </Avatar>
                    <Box>
                      <Typography variant="h6" fontWeight="bold" color="text.primary">
                        Manage Devices
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Monitor and configure devices
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  )
}

export default SuperAdminDashboard